import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GetStravaMeasurementSystemQuery,
  PageType,
} from '../../../../types/__generated__';
import { PageMetaContext } from '../../../context';
import {
  fetchPage,
  useFullPageQuery,
  useListTimelineEntries,
  useNotNullableContext,
  usePageOwner,
} from '../../../hooks';
import { getMeasurementSystemFromSettings } from '../../../utils/measurementSystem';
import { LazyLoader } from '../../LazyLoader/LazyLoader';
import PageSection from '../../PageSection/PageSection';
import getStravaMeasurementSystem from '../StravaTimelineEntry/GetStravaMeasurementSystem.gql';
import Timeline from '../Timeline/Timeline';

const TimelineUpdateEditorLazy = React.lazy(() =>
  import('../TimelineUpdateEditor/TimelineUpdateEditor'),
);

const TimelinePageSection: FunctionComponent = () => {
  const { isPageOwner } = usePageOwner();
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageShortName, pageType } = useNotNullableContext(PageMetaContext);
  const { data: settingsData } = useFullPageQuery<
    GetStravaMeasurementSystemQuery
  >(getStravaMeasurementSystem);

  const measurementSystem = getMeasurementSystemFromSettings(
    settingsData?.page?.strava?.settings,
  );

  const { loading, data, fetchMore } = useListTimelineEntries(
    measurementSystem,
  );

  if (loading || !data?.page) {
    return null;
  }

  const { timeline, legacyId } = data.page;

  const fetchNextPage = fetchPage(
    timeline?.pageInfo.endCursor ?? '',
    fetchMore,
  );

  const fetchNextPost = fetchPage(
    timeline?.pageInfo.endCursor ?? '',
    fetchMore,
    1,
  );

  // The new timeline service doesn't provide totalCount so counting nodes
  const entryCount = timeline
    ? Math.max(timeline.nodes.length, timeline.totalCount)
    : 0;

  return entryCount || isPageOwner ? (
    <PageSection
      title={t('page.timelineTitle')}
      titleId="updates"
      hasBorder={true}
    >
      {isPageOwner && (
        <LazyLoader>
          <TimelineUpdateEditorLazy
            pageId={legacyId}
            pageSlug={pageShortName}
            pageType={pageType.toUpperCase() as PageType}
            measurementSystem={measurementSystem}
          />
        </LazyLoader>
      )}
      {entryCount > 0 && (
        <Timeline
          fetchNextPage={fetchNextPage}
          fetchNextPost={fetchNextPost}
          timeline={timeline}
          owner={data.page.owner}
          measurementSystem={measurementSystem}
        />
      )}
    </PageSection>
  ) : null;
};

export default TimelinePageSection;
